import type { DeliveryMethodKind } from "@dransay/api/models";
import { useEffect, useMemo, useRef } from "react";
import { useFetcher } from "react-router";
import { buildVendorsFilteredMapInput } from "~/features/products/filters/sections/build-vendors-filtered-list-input";
import { usePostalCodeParam } from "~/hooks/use-postal-code-param";
import type {
	GetVendorsFilteredResult,
	VendorsFilteredItem,
	VendorsFilteredMetadata,
} from "./vendors-filtered.contract";

const VENDORS_FILTERED_ENDPOINT = "/api/vendors-filtered";

const EMPTY_ITEMS: VendorsFilteredItem[] = [];

export type AggregatedVendorCount = { top: number; all: number };

type VendorsFilteredResponse =
	| (GetVendorsFilteredResult & { aggregatedCount?: AggregatedVendorCount })
	| { error: string };

interface UseVendorsFilteredOptions {
	enabled?: boolean;
	deliveryMethodKind?: DeliveryMethodKind | null;
	/** Courier browse narrowed to express pharmacies open within 10 km. */
	expressCourierOnly?: boolean;
}

interface UseVendorsFilteredResult {
	items: VendorsFilteredItem[];
	metadata: VendorsFilteredMetadata | null;
	aggregatedCount: AggregatedVendorCount | null;
	isLoading: boolean;
	hasError: boolean;
	/**
	 * The express-courier availability check could not be completed, so the
	 * `expressCourierAvailable` flags on the rows are all `false` by default
	 * rather than by verdict. Lets an express filter tell "none here" apart
	 * from "we could not find out".
	 */
	expressCheckFailed: boolean;
	postalCode: string;
}

const hasItems = (
	response: VendorsFilteredResponse | undefined,
): response is GetVendorsFilteredResult & {
	aggregatedCount?: AggregatedVendorCount;
} => response != null && "items" in response;

/**
 * Fetches pharmacies for the current postal code through getVendorsFiltered.
 * Re-fetches when the postal code or delivery method changes.
 * Radius rows are bucketed client-side from this single page (MKP-2054).
 */
export function useVendorsFiltered({
	enabled = true,
	deliveryMethodKind,
	expressCourierOnly = false,
}: UseVendorsFilteredOptions = {}): UseVendorsFilteredResult {
	const { postalCode: urlPostalCode } = usePostalCodeParam();
	const postalCode = urlPostalCode ?? "";
	const fetcher = useFetcher<VendorsFilteredResponse>();
	const kindKey = deliveryMethodKind ?? "";
	const lastRequestKey = useRef("");

	// biome-ignore lint/correctness/useExhaustiveDependencies: fetcher is a new object every render; lastRequestKey prevents duplicate submits
	useEffect(() => {
		if (!enabled) {
			return;
		}
		const requestKey = `${postalCode}|${kindKey}|${expressCourierOnly}`;
		if (requestKey === lastRequestKey.current) {
			return;
		}
		lastRequestKey.current = requestKey;
		fetcher.submit(
			buildVendorsFilteredMapInput({
				deliveryMethodKind,
				postalCode,
				expressCourierOnly,
			}),
			{
				method: "POST",
				action: VENDORS_FILTERED_ENDPOINT,
				encType: "application/json",
			},
		);
	}, [enabled, postalCode, kindKey, deliveryMethodKind, expressCourierOnly]);

	const response = fetcher.data;
	const items = useMemo(
		() => (hasItems(response) ? response.items : EMPTY_ITEMS),
		[response],
	);
	const requestKey = `${postalCode}|${kindKey}|${expressCourierOnly}`;
	// Submit lives in an effect, so the first paint (and every key change
	// before the next response) is idle with either no data or the previous
	// payload. Treat that as loading so consumers do not resolve a default
	// radius from an empty set and flash `+25km` → `10km`.
	const isLoading =
		enabled &&
		(fetcher.state !== "idle" ||
			response == null ||
			lastRequestKey.current !== requestKey);

	return {
		items,
		metadata: hasItems(response) ? response.metadata : null,
		aggregatedCount: hasItems(response)
			? (response.aggregatedCount ?? null)
			: null,
		isLoading,
		hasError:
			fetcher.state === "idle" && response != null && !hasItems(response),
		expressCheckFailed: hasItems(response)
			? response.expressCheckFailed === true
			: false,
		postalCode,
	};
}
